import type { ComponentType } from "react";
import { Dialog, DialogBody, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog.tsx";
import { TabContent, Tabs, TabsList, TabsProvider, TabsTrigger } from "@/components/ui/tabs.tsx";
import GlobalsList from "@/features/diagnostics/components/globals/globals-list.tsx";
import HistoryStateList from "@/features/diagnostics/components/history-state/history-state-list.tsx";
import PartStatusList from "@/features/diagnostics/components/parts/part-status-list.tsx";
import PerformanceList from "@/features/diagnostics/components/performance/performance-list.tsx";
import ProductsList from "@/features/diagnostics/components/products/products-list.tsx";
import ProductReferenceList from "@/features/diagnostics/components/product-reference/product-reference-list.tsx";
import SmartNumberList from "@/features/diagnostics/components/smart-number/smart-number-list.tsx";
import useToggleDiagnostics from "@/features/hotkeys/hooks/use-toggle-diagnostics.ts";
import { typedObjectEntries } from "@/utils/types.ts";
import EventLogList from "./event-log/event-log-list.tsx";
import TrackingEventList from "./tracking-events/tracking-event-list.tsx";

const Diagnostic: Record<string, { tab: string; label: string; Component: ComponentType }> = {
    EventLogs: {
        tab: "event-log",
        label: "Event logs",
        Component: EventLogList,
    },
    Parts: {
        tab: "parts",
        label: "Parts",
        Component: PartStatusList,
    },
    Globals: {
        tab: "globals",
        label: "Globals",
        Component: GlobalsList,
    },
    Tracking: {
        tab: "tracking",
        label: "Tracking",
        Component: TrackingEventList,
    },
    History: {
        tab: "history",
        label: "History",
        Component: HistoryStateList,
    },
    SmartNumber: {
        tab: "smart-number",
        label: "Smart number",
        Component: SmartNumberList,
    },
    ProductReference: {
        tab: "product-reference",
        label: "Product reference",
        Component: ProductReferenceList,
    },
    Performance: {
        tab: "performance",
        label: "Performance",
        Component: PerformanceList,
    },
    Products: {
        tab: "products",
        label: "Products",
        Component: ProductsList,
    },
} as const;

const title = "Diagnostics";

export default function Diagnostics() {
    const [open, onOpenChange] = useToggleDiagnostics();

    // Prevent the dialog from leaking into the DOM
    if (!open) return null;

    return (
        <Dialog open onOpenChange={onOpenChange}>
            <DialogContent className="diagnostics">
                <DialogHeader>
                    <DialogTitle>{title}</DialogTitle>
                </DialogHeader>
                <DialogBody>
                    <TabsProvider initialTab={Diagnostic.EventLogs.tab}>
                        <Tabs className="diagnostics__tabs">
                            <TabsList>
                                {typedObjectEntries(Diagnostic).map(([key, value]) => (
                                    <TabsTrigger key={key} tab={value.tab}>
                                        {value.label}
                                    </TabsTrigger>
                                ))}
                            </TabsList>
                            {typedObjectEntries(Diagnostic).map(([key, value]) => (
                                <TabContent key={key} tab={value.tab}>
                                    <value.Component />
                                </TabContent>
                            ))}
                        </Tabs>
                    </TabsProvider>
                </DialogBody>
            </DialogContent>
        </Dialog>
    );
}
